import type { CartOutputDto } from "@sticker-giant/sg-frontend-common";

// TV Scientific pixel integration. The SDK is loaded site-wide on production only
// (mirrors the legacy Magento host gate), fires one universal site pixel per
// session, and fires a purchase pixel on the order confirmation page.

const TVSCI_SDK_ID = "tvsci-pixel-sdk";
const TVSCI_SDK_SRC = "https://assets.tvscipixel.com/tvscientific-pix-o-c8558499-bc9a-4e50-b781-2e218101158b.js";
const PROD_HOSTS = new Set(["stickergiant.com", "www.stickergiant.com"]);
const UNIVERSAL_FIRED_KEY = "tvsciPixelFired";

export function isTvSciProdHost() {
  return typeof window !== "undefined" && PROD_HOSTS.has(window.location.hostname);
}

let loadPromise: Promise<void> | null = null;

// Injects the SDK once and resolves when `window.tvsci` is available. Both the
// universal and purchase consumers await this, so the script loads a single time
// regardless of which mounts first.
export function loadTvSciPixel(): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const fail = () => reject(new Error("Failed to load TV Scientific pixel"));

    // Already executed — the SDK is on `window`, safe to resolve now.
    if (window.tvsci) {
      resolve();
      return;
    }

    // A tag exists but hasn't finished loading yet (e.g. after HMR reset).
    // Wait for its load event rather than resolving early against a missing SDK.
    const existing = document.getElementById(TVSCI_SDK_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", fail, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = TVSCI_SDK_ID;
    script.src = TVSCI_SDK_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = fail;
    document.body.appendChild(script);
  });

  return loadPromise;
}

// Fires once per browser session across the whole site.
export function fireUniversalSitePixel() {
  if (typeof window.tvsci === "undefined") return;
  if (sessionStorage.getItem(UNIVERSAL_FIRED_KEY)) return;
  window.tvsci.fireUniversalSitePixel();
  sessionStorage.setItem(UNIVERSAL_FIRED_KEY, "1");
}

// Fires once per order — guarded so a confirmation-page refresh doesn't
// double-count the conversion.
export function fireCompletePurchasePixel(cart: CartOutputDto) {
  if (typeof window.tvsci === "undefined") return;
  const firedKey = `tvsciPurchaseFired:${cart.id}`;
  if (sessionStorage.getItem(firedKey)) return;
  window.tvsci.fireCompletePurchasePixel({
    orderId: cart.id,
    orderAmount: String(cart.pricing.grandTotal),
    customerId: cart.customer.customerId,
    currency: "USD",
  });
  sessionStorage.setItem(firedKey, "1");
}
